import { useState, useEffect } from 'react'
import { AddOptions } from 'components/ideal-core-ui'
import s from './TimeTrackerModule.scss'
import { makeGetRequest, makePostRequest, makePutRequest, makeDeleteRequest } from 'utils/api'
import { AddButton } from 'components/common/AddButton'
import { useDispatch } from 'react-redux'
import { getUser, refreshToken, getToken, getTenant } from 'utils/authService'
import { toItems, toItem, toDealStatuses } from './serializers'
import { AddUser } from './AddUser'
import { useImmer } from 'use-immer'
import { List } from './List'
import Select from 'react-select'

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  roles: [
    {
      label: 'Time Tracker Manager',
      description:
        'Can view and edit other people’s entries; can run reports including any/all Time Tracker users',
      value: 'ROLE_TIMETRACKER_ADMIN',
      checked: true
    },
    {
      label: 'Time Tracker User',
      description: 'Can only view and edit their own time entries; reports show only their time',
      value: 'ROLE_TIMETRACKER',
      checked: false
    }
  ],
  hoursPerWeek: '',
  standardRate: '',
  overtimeRate: '',
  secondShiftRate: '',
  thirdShiftRate: '',
  standardCost: '',
  overtimeCost: '',
  secondShiftCost: '',
  thirdShiftCost: ''
}

const weekStartOptions = [
  { label: 'Sunday - Saturday', value: 'SUNDAY' },
  { label: 'Monday - Sunday', value: 'MONDAY' }
]

const TimeTrackerModule = ({ organizationId, iDealUsers, setIdealUsers }) => {
  const dispatch = useDispatch()
  const [formIsVisible, setFormIsVisible] = useState(false)
  const [existingUser, setExistingUser] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [error, setError] = useState('')
  const [editedUserDetails, setEditedUserDetails] = useImmer(null)
  const [timeTrackerUsers, setTimeTrackerUsers] = useState([])
  const [timeTrackerStatuses, setTimeTrackerStatuses] = useState([])
  const [selectedExistingUser, setSelectedExistingUser] = useState(null)
  const [dealStatuses, setDealStatuses] = useState([])
  const [settings, setSettings] = useState({})

  const loggedInUser = getUser()

  useEffect(() => {
    ;(async () => {
      const settingsResponse = await makeGetRequest('/v1/timetracker/settings')
      setSettings(settingsResponse)

      const { content } = await makeGetRequest('/v1/timetracker/users')
      setTimeTrackerUsers(content)

      const { statuses } = await makeGetRequest('/v1/timetracker/deal-statuses')
      setTimeTrackerStatuses(toDealStatuses(statuses))

      const response = await makeGetRequest('/v1/dealstatus')
      setDealStatuses(toDealStatuses(response))
    })()
  }, [])

  const openEditUser = async user => {
    const workInfo = await makeGetRequest(`/v1/timetracker/work-info/${user.id}`)
    setSelectedExistingUser({ value: user.id, label: user.userName })
    setExistingUser(true)
    setFormIsVisible(true)
    setIsEdit(true)
    setError('')
    setEditedUserDetails(() => ({
      id: user.id,
      firstName: user.firstName,
      email: user.email,
      lastName: user.lastName,
      roles: [
        {
          label: 'Time Tracker Manager',
          description:
            'Can view and edit other people’s entries; can run reports including any/all Time Tracker users',
          value: 'ROLE_TIMETRACKER_ADMIN',
          checked: user.roles.includes('ROLE_TIMETRACKER_ADMIN')
        },
        {
          label: 'Time Tracker User',
          description:
            'Can only view and edit their own time entries; reports show only their time',
          value: 'ROLE_TIMETRACKER',
          checked: user.roles.includes('ROLE_TIMETRACKER')
        }
      ],
      hoursPerWeek: workInfo.hoursPerWeek,
      standardRate: workInfo.standardRate,
      overtimeRate: workInfo.overtimeRate,
      secondShiftRate: workInfo.secondShiftRate,
      thirdShiftRate: workInfo.thirdShiftRate,
      standardCost: workInfo.standardCost,
      overtimeCost: workInfo.overtimeCost,
      secondShiftCost: workInfo.secondShiftCost,
      thirdShiftCost: workInfo.thirdShiftCost
    }))
  }

  const removeUser = async user => {
    try {
      const { user: deletedUser } = await makePostRequest('/v1/timetracker/work-info', {
        user: {
          userId: user.id,
          roles: user.roles.filter(role => !role.includes('ROLE_TIMETRACKER'))
        }
      })
      setTimeTrackerUsers(timeTrackerUsers.filter(u => u.id !== user.id))
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'User removed successfuly',
          level: 'success'
        }
      })

      // also make user inactive because it now has no roles
      if (deletedUser.roles.length === 0) {
        await makeDeleteRequest(`/v1/users/${deletedUser.userId}`)
        await makeDeleteRequest(
          `/v1/tenants?email=${encodeURIComponent(
            deletedUser.userEmail
          )}&x-token-tracking=${getToken()}`,
          {
            headers: {
              'x-tenant-schema': getTenant()
            }
          }
        )
      }

      if (loggedInUser.id === user.id) {
        refreshToken(() => {
          dispatch({
            type: 'REFRESH_HEADER'
          })
        })
      }
    } catch (e) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
  }

  const resetForm = () => {
    setFormIsVisible(true)
    setError('')
    setIsEdit(false)
    setEditedUserDetails(() => {
      return { ...initialState, organizationId }
    })
  }

  const changeDealStatus = async statuses => {
    try {
      const { statuses: enabledStatuses } = await makePostRequest('/v1/timetracker/deal-statuses', {
        enabledStatuses: statuses.map(status => status.value)
      })

      const filteredDealStatuses = dealStatuses.filter(status =>
        enabledStatuses.map(s => s.id).includes(status.value)
      )

      setTimeTrackerStatuses(filteredDealStatuses)

      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Deal status changed successfuly',
          level: 'success'
        }
      })
    } catch (e) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
  }

  const changeWeekStartDay = async value => {
    try {
      await makePutRequest('/v1/timetracker/settings', {
        weekStart: value.value
      })

      setSettings({ ...settings, weekStart: value.value })

      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Week start day changed successfuly',
          level: 'success'
        }
      })
    } catch (e) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <h5>Time Tracker Module</h5>
      <p className={s.description}>
        The iDeal Time Tracker module allows your organization to track where employees spend their
        time. Entries can be related to opportunities or projects or not. This way, you can also
        assess the profitability of your projects.
        <br />
        <br />
        The Time Tracker module price is $5/user/month. Time Tracker users don&#39;t necessarily
        have to be iDeal CRM users. For example you can have field workers as Time Tracker users.
        When they log into the system they will only see the Time Tracker tab where they can select
        the project they&#39;re working on and log their hours.
        <br />
        <br />
        If you have any questions about billing, please don&#39;t hesitate to contact Alex at
        alex@idealcrm.app
      </p>
      <hr />
      <AddOptions
        texts={{
          title: 'Type of Work',
          subtitle: 'Type of Work Options',
          description:
            "Enter as many options for type of work as needed so that reporting is granular enough for your organization and what you'd like to track."
        }}
        url="/v1/timetracker/type-of-work"
        toItemSerializer={toItem}
        toItemsSerializer={toItems}
      />
      <hr />
      <AddOptions
        texts={{
          title: 'Time off Types',
          subtitle: 'Time off Types Options',
          description:
            "Enter as many options for time off types as needed so that reporting is granular enough for your organization and what you'd like to track."
        }}
        url="/v1/timetracker/time-off-type"
        toItemSerializer={toItem}
        toItemsSerializer={toItems}
      />
      <h5>Charge Stages</h5>
      <p className={s.description}>Charge Time against the following stages</p>
      <Select
        isMulti
        onChange={values => changeDealStatus(values)}
        options={dealStatuses}
        className={s.select}
        value={timeTrackerStatuses}
      />
      <div style={{ marginTop: 30 }}>
        <h5>Week Start Day</h5>
        <p className={s.description}>Set week as</p>
        <Select
          onChange={value => changeWeekStartDay(value)}
          options={weekStartOptions}
          className={s.weekStartDaySelect}
          value={weekStartOptions.find(option => option.value === settings.weekStart)}
        />
      </div>
      <hr />
      <h5>Users</h5>
      <p className={s.description}>
        If a person is already an iDeal CRM user, use the{' '}
        <b>&quot;Add Existing User from iDeal CRM&quot;</b> button. If they are not part of iDeal
        CRM and you only want this person to user Time Tracker, use the{' '}
        <b>&quot;Add Time Tracker User Only&quot;</b> option.
      </p>
      <header className={s.header}>
        <AddButton
          onClick={() => {
            setExistingUser(true)
            resetForm()
          }}
          simpleBtn
          className={s.addUserBtn}
          disabled={(formIsVisible && existingUser) || isEdit}
        >
          Add Existing User from iDeal CRM
        </AddButton>
        <AddButton
          onClick={() => {
            setExistingUser(false)
            resetForm()
          }}
          simpleBtn
          disabled={(formIsVisible && !existingUser) || isEdit}
        >
          Add Time Tracker User only
        </AddButton>
      </header>
      {formIsVisible && (
        <AddUser
          selectedUser={selectedExistingUser}
          existingUser={existingUser}
          organizationId={organizationId}
          editedUserDetails={editedUserDetails}
          iDealUsers={iDealUsers}
          setTimeTrackerUsers={setTimeTrackerUsers}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          setFormIsVisible={setFormIsVisible}
          error={error}
          setError={setError}
          timeTrackerUsers={timeTrackerUsers}
          setSelectedUser={setSelectedExistingUser}
          setIdealUsers={setIdealUsers}
        />
      )}
      {timeTrackerUsers.length > 0 && (
        <List openEditUser={openEditUser} removeUser={removeUser} users={timeTrackerUsers} />
      )}
    </>
  )
}

export default TimeTrackerModule
